import { useEffect, useMemo, useState } from "react";
import "../../App.less";
import { Affix, Button, Modal, PageHeader, Space } from "antd";
import {
  ROUTE_CREATE_INVOICE,
  ROUTE_EDIT_INVOICE,
  ROUTE_RECEIVE_PAYMENT_ROOT,
} from "../../app/routes";
import { authSelector, refreshOrgState } from "../auth/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { useFirestore } from "react-redux-firebase";
import { invoices as invoicesQuerier } from "./../../app/services/firestore/queries/invoiceQueries";

import InternalNotesDrawer from "./components/InternalNotesDrawer";
import InvoiceEmailDrawer from "./components/InvoiceEmailDrawer";
import Menu from "@mui/material/Menu";

import { Box, Dialog, MenuItem, Tooltip } from "@mui/material";

import { i18n } from "../../i18n";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { useFirestoreWrite } from "../../app/services/hooks/fetch/useFirestoreWrite";
import { getColumns } from "./invoices-datagrid-columns";
import { ConfigureDuplicateInvoice } from "../../app/utils/models/configure/configureDuplicateInvoice";
import { INVOICES_COLLECTION } from "../../app/utils/models/collections/collectionConstants";
import { sleep } from "../../app/utils/Nifty/nifty";
import notificationError from "../../app/system-components/toasters/notificationError";
import { WarningFilled } from "@ant-design/icons";
import { COLOR_RED_0 } from "../../app/ui/colorConstants";
import notificationConfirm from "../../app/system-components/toasters/notificationConfirm";
import { CustomToolbar } from "./invoices-data-grid-toolbar";
import { useGetTableRowInvoicesForRangeQuery } from "../../app/services/api";
import { useInvoiceNumberListener } from "../../app/services/hooks/useInvoiceNumberListener";
import SendEmailTemplateView from "./send-email-template-view";
import { enqueueSnackbar } from "notistack";
import { checkSubscriptionLevel } from "../../app/interfaces/features-set-permissions";


const Invoices = ({ history }) => {

  const { userData, orgData, fsOrgPrefix } = useSelector(authSelector);
  const { nextInvoiceNumber } = useInvoiceNumberListener();

  const firestore = useFirestore();
  const dispatch = useDispatch();

  const [selectedRows, setSelectedRows] = useState([]);

  const [currentDates, setCurrentDates] = useState([
    null,
    null,
  ]);

  // Add column configuration state
  const [columnConfig, setColumnConfig] = useState([]);

  // Set initial values.
  useEffect(() => {

    const storedDateRange = sessionStorage.getItem('selectedDateRange');

    // Check if a date range was previously stored
    if (storedDateRange) {
      const parsedDateRange = JSON.parse(storedDateRange);

      parsedDateRange.startDate = new Date(parsedDateRange.startDate);
      parsedDateRange.endDate = new Date(parsedDateRange.endDate);

      setCurrentDates([
        parsedDateRange.startDate,
        parsedDateRange.endDate,
      ])

    } else {
      const oneYearLater = new Date();
      oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
      const twoMonthAgo = new Date();
      twoMonthAgo.setMonth(twoMonthAgo.getMonth() - 2);

      setCurrentDates([
        twoMonthAgo,
        oneYearLater,
      ])
    }

  }, [])

  const [searchedInvoices, setSearchedInvoices] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);

  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState();
  const [contextMenu, setContextMenu] = useState(null);
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false);

  const openEmailTemplateDialog = () => {
    setOpenTemplateDialog(true);
  }

  const {
    data: tableRowInvoices,
    isFetching: fetchingInvoices,
    refetch: refetchInvoices,
  } = useGetTableRowInvoicesForRangeQuery({
    db: { firestore },
    orgPrefix: fsOrgPrefix,
    dateStart: currentDates[0] ?? null,
    dateEnd: currentDates[1] ?? null,
    orgTimezone: orgData?.orgTimezone,
  });

  useEffect(() => {
    if (!orgData || !firestore) {
      return;
    }

    refetchInvoices()
  }, [orgData, currentDates]);

  const apiRef = useGridApiRef();

  const { deleteDocument, newDocument, updateOrgDocument } = useFirestoreWrite();

  const [chatDrawer, setChatDrawer] = useState({
    visible: false,
    invoice: null,
  });
  const handleChatClick = (i) => {
    setChatDrawer({
      visible: true,
      invoice: i,
    });
  };

  const [emailDrawer, setEmailDrawer] = useState({
    visible: false,
    invoice: null,
  });
  const handleEmailsClick = (i) => {
    setEmailDrawer({
      visible: true,
      invoice: i,
    });
  };

  const muiInvoiceTableColumns = useMemo(
    () => getColumns(orgData, handleEmailsClick, handleChatClick),
    [orgData]
  );

  // Initialize column configuration when columns are available
  useEffect(() => {
    if (muiInvoiceTableColumns && muiInvoiceTableColumns.length > 0 && columnConfig.length === 0) {
      try {
        // Try to load saved column configuration
        const savedColumnMeta = JSON.parse(localStorage.getItem("INVOICE_COLUMNS_CONFIG"));
        
        if (savedColumnMeta && Array.isArray(savedColumnMeta) && savedColumnMeta.length > 0) {
          // Merge saved metadata (order, visibility) with original column definitions (to preserve functions)
          const mergedColumns = muiInvoiceTableColumns.map(origCol => {
            // Find this column in saved config
            const savedCol = savedColumnMeta.find(sc => sc.field === origCol.field);
            if (savedCol) {
              // Return original column with visibility from saved config
              return {
                ...origCol,
                visible: savedCol.visible
              };
            }
            return origCol;
          });
          
          // Sort columns according to the saved order
          const sortedColumns = [];
          savedColumnMeta.forEach(savedCol => {
            const col = mergedColumns.find(c => c.field === savedCol.field);
            if (col) {
              sortedColumns.push(col);
            }
          });
          
          setColumnConfig(sortedColumns);
        } else {
          setColumnConfig(muiInvoiceTableColumns);
        }
      } catch (error) {
        console.error("Error loading saved column config:", error);
        setColumnConfig(muiInvoiceTableColumns);
      }
    }
  }, [muiInvoiceTableColumns, columnConfig.length]);

  // Handler for updating column configuration
  const handleUpdateColumns = (newColumnConfig) => {
    setColumnConfig(newColumnConfig);
    
    // Save only necessary metadata (not functions) to localStorage
    const columnMetadata = newColumnConfig.map(col => ({
      field: col.field,
      visible: col.visible,
      headerName: col.headerName
    }));
    
    localStorage.setItem("INVOICE_COLUMNS_CONFIG", JSON.stringify(columnMetadata));
    
    // Apply visibility and order to grid
    if (apiRef.current) {
      const visibilityModel = {};
      newColumnConfig.forEach(col => {
        if (col.field) {
          visibilityModel[col.field] = col.visible !== false;
        }
      });
      apiRef.current.setColumnVisibilityModel(visibilityModel);
    }
  };

  const handleDuplicate = async () => {
    setContextMenu(null);

    if (selectedRow) {
      setLoading(true);

      const dupInvoiceSnap = invoicesQuerier(
        orgData?.orgTimezone
      ).fetchInvoiceRawData({ firestore }, fsOrgPrefix, {
        id: selectedRow && selectedRow,
      });

      dupInvoiceSnap
        .then((res) => {
          console.log("res", res);

          const payload = ConfigureDuplicateInvoice(
            res,
            userData,
            nextInvoiceNumber
          );

          updateOrgDocument({
            data: {
              payload: {
                currentInvoiceCount: nextInvoiceNumber,
              },
              id: orgData && orgData.id,
            },
          })
            .then(() => {
              newDocument({
                data: { collection: INVOICES_COLLECTION, payload },
              })
                .then(async () => {
                  await sleep(2_000);
                  setLoading(false);
                  onDuplicateComplete();
                })
                .catch((err) => {
                  console.log(err);
                  setLoading(false);
                });
            })
            .catch((err) => {
              console.log(err);
              setLoading(false);
              notificationError("Something went wrong");
            });
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    }
  };

  const handleDelete = () => {
    setContextMenu(null);
    Modal.confirm({
      icon: <WarningFilled style={{ color: COLOR_RED_0 }} />,
      title: "Delete invoice?",
      content:
        "This cannot be undone! Please note, deleting an invoice will remove it from your account completely and it cannot be recovered. It will immediately affect all associated reporting tied to this transaction (ie. QuickReport, Available Inentory, Routes...etc.) All linked payments will remain and it may affect your books. Be sure to compare transactions in QuickBooks to ensure proper account balancing.",
      okType: "danger",
      async onOk() {
        try {
          return await new Promise(async (resolve, reject) => {
            if (!fsOrgPrefix) {
              return notificationError("Something went wrong", "Please try again later");
            } else {
              await firestore.collection("orgs").doc(orgData.id).collection("tableRowInvoices").doc(selectedRow).delete();
              await deleteDocument({
                data: {
                  collection: INVOICES_COLLECTION,
                  id: selectedRow && selectedRow,
                },
              });

              resolve();
              onDeleteComplete();
              notificationConfirm("Invoice deleted", "");
            }
          });
        } catch (err) {
          console.log(err);
          notificationError("Something went wrong", "Please try again later");
        }
      },
      onCancel() { },
    });
  };

  const handleReceivePayment = () => {
    const invoice = tableRowInvoices.find((invoice) => invoice.id === selectedRow);

    if (invoice) {
      const customer = invoice.customerQueryHook;
      const invoiceId = invoice.id;
      history.push({
        pathname: `${ROUTE_RECEIVE_PAYMENT_ROOT}${customer}`,
        state: { invoiceId: invoiceId },
      });
    }
  };

  const handleView = () => {
    history.push(`${ROUTE_EDIT_INVOICE}${selectedRow && selectedRow}`);
  };

  const handleDoubleClickMenu = (params, event) => {
    setSelectedRow(params.id);
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
        : null
    );
  };

  const handleContextMenu = (event) => {
    event.preventDefault();
    setSelectedRow(event.currentTarget.getAttribute("data-id"));
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
        : null
    );
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  const onDeleteComplete = () => {
    refetchInvoices();
  };

  const onDuplicateComplete = async () => {
    dispatch(refreshOrgState({ firestore }, orgData));
  };

  const saveStateToLocalStorage = debounce((state) => {
    localStorage.setItem("CURRENT_GRID_SETTINGS", JSON.stringify(state));
  }, 500); 
  

  return (
    <div>
      <Affix>
        <PageHeader
          className="PageHeader"
          title={i18n("invoices.sales")}
          extra={[
            <div>
              <Space size={12} className="make-the-sale-header">
                {selectedRows.length > 0 && (
                  <Tooltip 
                    title={!checkSubscriptionLevel("makeTheSale.emailTemplates", orgData) ? 
                    "You do not have the required plan for sending email templates. Requires Plus Plan, you can change this in Admin/Billing" :
                     "" 
                    }
                  >
                  <Button
                    disabled={!checkSubscriptionLevel("makeTheSale.emailTemplates", orgData)}
                    type="primary"
                    onClick={openEmailTemplateDialog}
                  >
                    Send Emails
                  </Button>
                  </ Tooltip>
                )}

                <Button
                  type="primary"
                  onClick={() => {
                    history.push(ROUTE_CREATE_INVOICE);
                  }}
                >
                  {i18n("invoices.newOrder")}
                </Button>
              </Space>
            </div>,
          ]}
        />
      </Affix>
      <div className="make-the-sale-invoices">
        <div style={{ width: "100%", paddingLeft: "8px", paddingRight: "8px" }}>
          {orgData && apiRef && tableRowInvoices && (
            <Box
              sx={{  height: "calc(100vh - 100px)", // Adjust based on header/footer height
                display: "flex", 
                flexDirection: "column",
                width: "100%", 
                backgroundColor: "white" 
              }}
            >
              <DataGridPro
                disableRowSelectionOnClick
                autoHeight={false}
                style={{ height: "100%", flex: 1, paddingLeft: "8px" }}
                ignoreDiacritics={true}
                apiRef={apiRef}
                rowBuffer={15}
                rowThreshold={5}
                pagination={true}
                loading={fetchingInvoices || loading}
                columns={columnConfig.length > 0 ? 
                  columnConfig.filter(col => col.visible !== false) : 
                  muiInvoiceTableColumns
                }
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 25 },
                  },
                  sorting: {
                    sortModel: [{ field: "invoiceNumber", sort: "desc" }],
                  },
                }}
                slots={{
                  noResultsOverlay: () => (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <h2>
                        No results found. Try expanding the date range if you
                        are not finding what you expect.
                      </h2>
                    </div>
                  ),
                  toolbar: () =>
                    CustomToolbar(
                      apiRef, 
                      currentDates, 
                      setCurrentDates, 
                      orgData,
                      columnConfig.length > 0 ? columnConfig : muiInvoiceTableColumns, 
                      handleUpdateColumns
                    ),
                }}
                onStateChange={(state) => {
                  saveStateToLocalStorage(apiRef.current.exportState());
                }}
                onRowSelectionModelChange={(params) => {
                  setSelectedRows(params);
                }}
                onRowDoubleClick={(params, event) =>
                  handleDoubleClickMenu(params, event)
                }
                slotProps={{
                  row: {
                    onContextMenu: handleContextMenu,
                    style: { cursor: "context-menu" },
                  },
                }}
                rows={tableRowInvoices}
                checkboxSelection={true}
              />
            </Box>
          )}

          <Menu
            open={contextMenu !== null}
            onClose={handleClose}
            anchorReference="anchorPosition"
            anchorPosition={
              contextMenu !== null
                ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                : undefined
            }
            slotProps={{
              root: {
                onContextMenu: (e) => {
                  e.preventDefault();
                  handleClose();
                },
              },
            }}
          >
            <MenuItem onClick={handleView}>View</MenuItem>
            <MenuItem onClick={handleDuplicate}>Duplicate</MenuItem>
            {/* <MenuItem onClick={handleReceivePayment}>Receive Payment</MenuItem> */}
            <MenuItem onClick={handleDelete}>Delete</MenuItem>
          </Menu>

          <Dialog onClose={() => setOpenTemplateDialog(false)} open={openTemplateDialog}>
            <SendEmailTemplateView 
            orgData={orgData} 
            userData={userData}
            invoiceIds={selectedRows} 
            close={() => setOpenTemplateDialog(false)} 
            complete={() => {
              enqueueSnackbar("Sending Emails. Results will be sent to Dashboard notifications", {
                variant: "success",
              });
              setOpenTemplateDialog(false)
            }}/>
          </Dialog>

          {orgData && (
            <InternalNotesDrawer
              showTags={false}
              drawerView
              visible={chatDrawer.visible}
              handleCloseDrawer={setChatDrawer}
              invoice={chatDrawer.invoice}
              tags={orgData.tags}
              onComplete={() => {
                setChatDrawer({
                  visible: false,
                  item: null,
                });
              }}
            />
          )}

          <InvoiceEmailDrawer
            drawerView
            visible={emailDrawer.visible}
            handleCloseDrawer={() =>
              setEmailDrawer({
                visible: false,
                item: null,
              })
            }
            invoice={emailDrawer.invoice}
            onComplete={() => {
              setEmailDrawer({
                visible: false,
                item: null,
              });
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Invoices;

function debounce(func, wait) {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

